import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './shadcn/card';
import { Button } from './shadcn/button';
import { Alert, AlertDescription } from './shadcn/alert';
import { post } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { verifyToken } from '../services/authService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PLAN_DETAILS = {
  platform_only: { displayName: 'Platform Single Plan', priceMonthly: 20 },
  platform_only_family: { displayName: 'Platform Family Plan', priceMonthly: 30 },
};

/**
 * Card form rendered inside <Elements> (bound to the subscription's first-invoice
 * PaymentIntent). Confirming charges the card now and activates the subscription;
 * the card is saved as the default so renewals auto-charge.
 */
function PayNowForm({ planDetails }) {
  const stripe = useStripe();
  const elements = useElements();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/start-subscription` },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment failed. Please check your card details and try again.');
      setProcessing(false);
      return;
    }

    // Payment succeeded. Refresh the user so accessStatus reflects the new
    // subscription (webhooks set has_access on invoice.paid).
    try {
      const token = Cookies.get('token');
      const freshUserData = await verifyToken(token);
      login(freshUserData);
    } catch (err) {
      console.error('Failed to refresh user after payment:', err);
    }
    navigate('/');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
      >
        {processing ? 'Processing payment...' : `Pay £${planDetails.priceMonthly} & start subscription`}
      </Button>
      <p className="text-xs text-center text-gray-500">
        You'll be charged £{planDetails.priceMonthly} today and then monthly. Cancel anytime from Manage Subscription.
      </p>
    </form>
  );
}

function StartPlatformSubscription() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | already_active | error

  const planDetails = PLAN_DETAILS[user?.selectedPlan] || PLAN_DETAILS.platform_only;

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const res = await post('/payments/start-platform-subscription', {}, true);
        if (cancelled) return;
        if (res?.alreadyActive || !res?.clientSecret) {
          setStatus('already_active');
        } else {
          setClientSecret(res.clientSecret);
          setStatus('ready');
        }
      } catch (err) {
        console.error('Failed to start platform subscription:', err);
        if (!cancelled) setStatus('error');
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Start your subscription</CardTitle>
          <CardDescription>
            {planDetails.displayName} — £{planDetails.priceMonthly}/month
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'loading' && <div>Loading...</div>}

          {status === 'error' && (
            <Alert variant="destructive">
              <AlertDescription>
                Couldn't start checkout. Please refresh and try again, or contact us if it persists.
              </AlertDescription>
            </Alert>
          )}

          {status === 'already_active' && (
            <div className="space-y-4">
              <p className="text-gray-700">Your subscription is already active.</p>
              <Button className="w-full" onClick={() => navigate('/')}>
                Continue
              </Button>
            </div>
          )}

          {status === 'ready' && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PayNowForm planDetails={planDetails} />
            </Elements>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default StartPlatformSubscription;
